import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";

import { snapshotPath } from "./EnvironmentsPage";
import {
  type GoverningAttribute,
  type Row,
  type Scope,
  type SourceReference,
  governingAttribute,
  shown,
} from "./profileClient";
import { useLatestRequest } from "./latest";
import { cell, originLabel } from "./SectionTable";

function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}

function Facts({ caption, rows }: { caption: string; rows: [string, string][] }) {
  return (
    <table className="grid">
      <caption>{caption}</caption>
      <tbody>
        {rows.map(([label, value]) => (
          <tr key={label}>
            <th scope="row">{label}</th>
            <td>{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function sourceRows(source: SourceReference): [string, string][] {
  return [
    ["Profile version", source.profile_version],
    ["Snapshot", source.snapshot_id],
    ["Section", source.section],
    ["Row", source.row_key],
    ["Collection method", source.collection_method],
    ["Collection source", source.collection_source],
    ["Source SHA-256", source.source_sha256],
    ["Seal SHA-256", source.seal_sha256],
  ];
}

/** Maximo types whose scale is part of the definition (package.SCALE_TYPES): there an explicit 0
 * (whole numbers) is shown. Other types record a scale of 0 that limits nothing. */
const SCALE_TYPES = new Set(["DECIMAL", "FLOAT", "AMOUNT", "DURATION"]);

export function typeText(d: Row): string {
  const scaled =
    d.scale !== null &&
    d.scale !== undefined &&
    (d.scale !== 0 || SCALE_TYPES.has(String(d.maxtype)));
  const size = d.length == null ? "" : ` ${d.length}${scaled ? `,${cell(d.scale)}` : ""}`;
  return `${cell(d.maxtype)}${size}`;
}

function RowsTable({ caption, rows, columns }: { caption: string; rows: Row[]; columns: string[] }) {
  if (rows.length === 0) return null;
  return (
    <table className="grid">
      <caption>{caption}</caption>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index}>
            {columns.map((c) => (
              <td key={c}>{cell(row[c])}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Behavior({ answer }: { answer: GoverningAttribute }) {
  const a = answer.profile;
  const d = a.definition ?? {};
  const o = a.object;
  const domain = a.domain;
  return (
    <>
      <Facts
        caption="Destination behavior (the environment profile governs)"
        rows={[
          ["Type", typeText(d)],
          ["Required", cell(d.required)],
          ["Persistent", cell(d.persistent)],
          ["Writability", a.writability.replace("_", " ")],
          ["Default value", cell(d.defaultvalue)],
          ["Same as", cell(a.same_as)],
          ["Key position", cell(a.key_position)],
          ["Origin", `${originLabel(a.origin)}${a.origin ? ` (${a.origin.basis})` : ""}`],
          ["Support level", cell(a.support_level)],
        ]}
      />
      <Facts
        caption="Scope"
        rows={[
          ["Object", `${o.objectname} (${o.status})`],
          ["Scope level", cell(o.scope_level)],
          ["Scope columns", o.scope_dimensions ? o.scope_dimensions.join(", ") || "none (system)" : "unknown"],
          ["Key columns", o.key_columns.join(", ") || "—"],
          ["Object persistent", cell(o.definition?.persistent)],
        ]}
      />
      {domain && (
        <>
          <Facts
            caption="Domain behavior"
            rows={[
              ["Domain", `${domain.domainid} (${cell(domain.definition?.domaintype)})`],
              ["Values", domain.values_status],
              [
                "Valid in scope",
                domain.values_status === "profiled"
                  ? domain.valid_in_scope.join(", ") || "none"
                  : `unknown: ${domain.reason ?? domain.values_status}`,
              ],
              [
                "Scope used",
                Object.entries(domain.scope)
                  .map(([k, v]) => `${k}=${v ?? "any"}`)
                  .join(", ") || "none: values without organization or site only",
              ],
            ]}
          />
          <RowsTable
            caption={`All values of ${domain.domainid}`}
            rows={domain.values}
            columns={["value", "maxvalue", "orgid", "siteid", "description"]}
          />
        </>
      )}
      <RowsTable
        caption="Security restrictions"
        rows={a.security_restrictions}
        columns={["restricttype", "groupname", "app", "conditionnum"]}
      />
      <RowsTable
        caption="Automation"
        rows={a.automation}
        columns={["autoscript", "launchpointname", "launchpointtype", "active"]}
      />
      {a.source && <Facts caption="Traced to" rows={sourceRows(a.source)} />}
    </>
  );
}

function Generic({ answer }: { answer: GoverningAttribute }) {
  const g = answer.generic_reference;
  return (
    <section aria-label="Generic reference">
      <h2>Generic reference (Maximo Base Pack)</h2>
      {answer.generic_status === "no_pack" && <p>No Maximo Base Pack is installed.</p>}
      {answer.generic_status === "not_in_pack" && (
        <p>The Base Pack has no entry for this attribute; the profile alone defines it.</p>
      )}
      {g && (
        <Facts
          caption={`${g.pack_key} ${g.pack_version}, entry ${g.entry_id}`}
          rows={Object.entries(g.definition).map(([k, v]) => [k, cell(v)])}
        />
      )}
      {answer.discrepancies.length > 0 && (
        <table className="grid">
          <caption>Differences from the generic reference</caption>
          <thead>
            <tr>
              <th>Category</th>
              <th>Difference</th>
              <th>Environment profile (governs)</th>
              <th>Base Pack</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {answer.discrepancies.map((d) => (
              <tr key={d.id}>
                <td>{d.category}</td>
                <td>{d.difference}</td>
                <td>{shown(d.profile_value)}</td>
                <td>{shown(d.pack_value)}</td>
                <td>
                  {d.status}
                  {d.resolution ? ` (${d.resolution})` : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/** One target attribute traced to the exact environment metadata that governs it (MER-D-015):
 * definition, scope, requiredness, persistence, writability, and domain behavior, with the
 * profile row it comes from and the generic Base Pack reference beside it. */
export function AttributeTracePage() {
  const { snapshotId = "", objectname = "", attributename = "" } = useParams();
  const [scope, setScope] = useState<Scope>({});
  const [form, setForm] = useState<Scope>({});
  const [answer, setAnswer] = useState<GoverningAttribute | null>(null);
  const [error, setError] = useState<string | null>(null);
  const begin = useLatestRequest();

  const load = useCallback(
    async (wanted: Scope) => {
      const current = begin();
      setError(null);
      try {
        const found = await governingAttribute(snapshotId, objectname, attributename, wanted);
        if (!current()) return;
        setAnswer(found);
        setScope(wanted);
      } catch (caught) {
        if (current()) setError(message(caught));
      }
    },
    [snapshotId, objectname, attributename, begin],
  );

  useEffect(() => {
    void load({});
  }, [load]);

  function onScope(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void load({
      orgid: form.orgid?.trim().toUpperCase() || undefined,
      siteid: form.siteid?.trim().toUpperCase() || undefined,
    });
  }

  const profile = answer?.profile;
  return (
    <section>
      <p>
        <Link to={snapshotPath(snapshotId)}>Back to the profile version</Link>
      </p>
      <h1>
        {objectname.toUpperCase()}.{attributename.toUpperCase()}
      </h1>
      {error && <p role="alert">{error}</p>}
      <form onSubmit={onScope} aria-label="Scope">
        <label>
          Organization{" "}
          <input
            value={form.orgid ?? ""}
            onChange={(event) => setForm({ ...form, orgid: event.target.value })}
          />
        </label>{" "}
        <label>
          Site{" "}
          <input
            value={form.siteid ?? ""}
            onChange={(event) => setForm({ ...form, siteid: event.target.value })}
          />
        </label>{" "}
        <button type="submit">Apply scope</button>
      </form>
      {answer && profile && (
        <>
          <p>
            Governed by the environment profile {profile.source?.profile_version ?? ""}
            {scope.orgid || scope.siteid
              ? ` in scope ${[scope.orgid, scope.siteid].filter(Boolean).join("/")}`
              : ""}
            .
          </p>
          {profile.status === "profiled" ? (
            <Behavior answer={answer} />
          ) : (
            <p>
              {profile.status === "absent" ? "Not in this environment" : "Unknown"}:{" "}
              {profile.reason}
            </p>
          )}
          <Generic answer={answer} />
        </>
      )}
    </section>
  );
}
